import type { PropsWithChildren } from "react";

import styled from "styled-components";

import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ChartTooltip, ChartTooltipSubTitle, PopupHoverContainer, Button } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { UrlCellWithTooltipContainer } from "app/components/React/Table/cells/UrlCell";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import type { Translate } from "app/filters/i18nFilter";
import { translate } from "app/filters/i18nFilter";
import { cpcFilter, minAbbrNumberFilter, smallNumbersPercentageFilter } from "app/filters/ngFilters";
import { calcDomainsPositions } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Utilities";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { createMultiIncludeExcludeFilter } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/utils";
import type { IWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import DurationService from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { FlexRow, FlexRowReverse } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { formatPercentageWithDynamicPrecision } from "./table/changeValueFormatters";
import type {
    IData,
    IDataRecord,
    ISitesData,
    IWebsiteKeywordsTransformDataReturn,
} from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";

export const filterAndSortColumnsForTable = (columns: any[], savedStateColumns: any[]) => {
    return columns.sort((a, b) => {
        const aIndex = savedStateColumns.findIndex((col) => col.field === a.field);
        const bIndex = savedStateColumns.findIndex((col) => col.field === b.field);

        if (aIndex === -1 || bIndex === -1) {
            return 0;
        }
        return bIndex > aIndex ? -1 : 1;
    });
};

const getSiteDisplayValues = ({ percentage, value }, minVisitsAbbrFilter) => {
    percentage = percentage * 100;
    let percentageText = `${percentage.toFixed(2)}%`;

    const valueText = minVisitsAbbrFilter(value);
    if (percentage > 0 && percentage < 0.01) {
        percentage = 0.01;
        percentageText = `< ${percentage}%`;
    } else if (percentage > 99.99 && percentage < 100) {
        percentage = 99.99;
        percentageText = `> ${percentage}%`;
    }
    return {
        percentage,
        percentageText,
        value,
        valueText,
    };
};

export const getItemsFromBreakdown = (
    breakdown: any,
    sitesList: any,
    displayType: any,
    minVisitsAbbrFilter: any,
    metricBarNameMap,
) => {
    const data = [];
    for (const [metric, metricDadata] of Object.entries(breakdown)) {
        const metricDisplayName = metric in metricBarNameMap ? metricBarNameMap[metric] : metric;
        const metricOverviewObject = { metric: metricDisplayName, bars: [] };
        for (const [siteKey, siteData] of Object.entries(metricDadata)) {
            const { bars } = metricOverviewObject;
            const siteInfo = sitesList.find(({ url, name }) => (url ? url === siteKey : name === siteKey)) ?? {};
            const { percentage, percentageText, valueText } = getSiteDisplayValues(siteData, minVisitsAbbrFilter());

            switch (displayType) {
                case "percent":
                    bars.push({
                        ...siteInfo,
                        name: siteInfo.url ?? siteInfo.name,
                        valueText: percentageText,
                        width: percentage,
                    });
                    break;
                case "number":
                    bars.push({
                        ...siteInfo,
                        name: siteInfo.url ?? siteInfo.name,
                        valueText,
                        width: percentage,
                    });
                    break;
            }
        }
        data.push(metricOverviewObject);
    }
    return data;
};

export const TABLE_COLUMNS_PREFERENCES_KEY = "websiteKeywordsTableColumns";

const getTooltipColumnDefinition = () => [
    {
        key: "url",
        title: translate("website.keywords.2.0.table.top.url.tooltip.url.title"),
        selector: "url" as const,
        align: "left",
        size: ChartTooltip.LabelCellDefaultWidth,
        render: ({ url }) => {
            return (
                <ChartTooltip.LabelCellWithLinkOut
                    label={url.label}
                    backgroundColor={url.color}
                    link={url.label}
                />
            );
        },
    },
];

const MoreSites = styled.div`
    margin-left: 9px;
    color: ${colorsPalettes.carbon[200]};
    cursor: default;
    height: 26px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ChartTooltipStyled = styled(ChartTooltip)`
    min-width: 200px;
    max-width: 850px;
    ${ChartTooltipSubTitle} {
        margin-bottom: 0px;
    }
` as React.ComponentType as typeof ChartTooltip;

export const getCustomContainerForTopUrlsTooltip = (
    values: { Key: string; Value: string }[],
    keyword: string,
    targetState = "organicsearch_website_landingpages_v2",
) =>
    function ({ children }: PropsWithChildren) {
        const handleNavigation = () => {
            const baseParams = {
                ...proNavigator.getParams(),
                selectedDomain: values[0].Key,
                multiIncludeExcludeKeywords: createMultiIncludeExcludeFilter(keyword, "keyword"),
            };
            void proNavigator.go(targetState, baseParams);
        };

        return (
            <PopupHoverContainer
                content={() => (
                    <ChartTooltipStyled
                        columnDefinition={getTooltipColumnDefinition()}
                        data={values.map((site) => ({
                            url: {
                                label: site.Value,
                                color: chosenSites.getSiteColor(site.Key),
                            },
                        }))}
                        title={undefined}
                    >
                        <FlexRowReverse>
                            <Button
                                type="flat"
                                onClick={handleNavigation}
                            >
                                {translate("website.keywords.2.0.table.top.url.tooltip.cta")}
                            </Button>
                        </FlexRowReverse>
                    </ChartTooltipStyled>
                )}
                config={{ allowHover: true, placement: "left", closeDelay: 150 }}
            >
                <UrlCellWithTooltipContainer>
                    <FlexRow>{children}</FlexRow>
                    {values.length > 1 ? <MoreSites>+{values.length - 1}</MoreSites> : null}
                </UrlCellWithTooltipContainer>
            </PopupHoverContainer>
        );
    };

const getExtractedDataFromSitesData = (
    dataUnit: IDataRecord,
): {
    siteSerpFeatures: Record<string, string[]>;
    siteOrigins: Record<string, number>;
    previousPosition: Record<string, number | null>;
    position: Record<string, number | null>;
    siteClicks: Record<string, number | null>;
} => {
    if (!Array.isArray(dataUnit.SitesData)) {
        return { siteSerpFeatures: {}, siteOrigins: {}, previousPosition: {}, position: {}, siteClicks: {} };
    }
    const siteSerpFeatures = {};
    const siteOrigins = {};
    const siteClicks = {};
    const previousPosition: Record<string, number | null> | number = {};
    const position: Record<string, number | null> | number = {};
    dataUnit.SitesData.forEach((site: ISitesData) => {
        siteSerpFeatures[site.Site] = site.SerpFeatures;
        siteOrigins[site.Site] = site.Share;
        siteClicks[site.Site] = site.Value;
        previousPosition[site.Site] = site.PreviousPosition ?? null;
        position[site.Site] = site.Position ?? null;
    });
    return { siteSerpFeatures, siteOrigins, previousPosition, position, siteClicks };
};

export const transformData = (data: IData, duration: string): IWebsiteKeywordsTransformDataReturn => {
    const transformedData = data.Data.Records.map((dataUnit: IDataRecord) => {
        const { siteSerpFeatures, siteOrigins, previousPosition, position, siteClicks } =
            getExtractedDataFromSitesData(dataUnit);
        const shareOfDesktop = dataUnit.DesktopClicks / dataUnit.Clicks;
        const organicClicksShare = dataUnit.OrganicClicks / dataUnit.Clicks;
        const durationText = DurationService.getFormattedDateOutOfDurationParam(duration, shortMonthAndYear);
        return {
            key: dataUnit.Keyword,
            ...dataUnit,
            keyword: dataUnit.Keyword,
            sitesData: dataUnit.SitesData,
            cpc: cpcFilter()(dataUnit.Cpc),
            // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'string'
            zeroClicksShare: smallNumbersPercentageFilter()(dataUnit.ZeroClicksShare || 0, 2),
            share: { totalClicks: getVolumeValue(dataUnit.Clicks), value: getShareValue(dataUnit.Share) },
            previousClicksPop: {
                totalClicks: getVolumeValue(dataUnit.PreviousClicksPop),
                value: getShareValue(dataUnit.PreviousSharePop),
            },
            kwWindowVolume: minAbbrNumberFilter()(
                getVolumeValue(dataUnit.KwWindowVolume),
                THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
            ) as string,
            kwVolume: minAbbrNumberFilter()(
                getVolumeValue(dataUnit.KwVolume),
                THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
            ) as string,
            kwVolumeAverage: minAbbrNumberFilter()(
                getVolumeValue(dataUnit.KwVolumeAverage),
                THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
            ) as string,
            clicksChange: {
                first: dataUnit.PreviousClicks,
                last: dataUnit.Clicks,
                change: dataUnit.Change,
                changeState: dataUnit.ChangeState,
                changeValueFormatted: formatPercentageWithDynamicPrecision(dataUnit.Change),
            },
            volumeChange: {
                first: dataUnit?.KwWindowVolume,
                last: dataUnit?.PreviousVolume,
                change: dataUnit?.VolumeChange,
                changeState: dataUnit?.VolumeChangeState,
                changeValueFormatted: formatPercentageWithDynamicPrecision(dataUnit?.VolumeChange),
            },
            searchTerm: dataUnit.Keyword,
            webSourceShare: shareOfDesktop,
            organicClicksShare,
            serpScrapeDate: dataUnit.LatestKeywordSerpDate,
            siteSerpFeatures,
            siteOrigins,
            siteClicks,
            positionLeader: calcDomainsPositions(dataUnit.SitesData),
            previousPosition,
            currentPosition: position,
            urlCount: dataUnit.UrlCount,
            position: dataUnit.SitesData.map((site: ISitesData) => {
                return {
                    Key: site.Site,
                    Value: site.Position ?? -1,
                };
            }),
            topUrl: dataUnit.TopUrl,
            topUrls: dataUnit.SitesData.map((site: ISitesData) => {
                return {
                    Key: site.Site,
                    Value: site.TopUrl ?? "",
                };
            }),
            intent: {
                PrimaryIntent: dataUnit.PrimaryIntent,
                SecondaryIntent: dataUnit.SecondaryIntent,
            },
            durationText,
            difficulty: dataUnit.Difficulty,
            competition: dataUnit.Competition,
            getDifficultyBarTexts: (translateFunc: Translate, difficultyLevel: string, country: string) => {
                return {
                    labelText: translateFunc(`keyword.difficulty.level.${difficultyLevel}.label`),
                    easyLevelText: translateFunc("keyword.difficulty.level.easy.label"),
                    difficultLabelText: translateFunc("keyword.difficulty.level.difficult.label"),
                    disclaimerText: translateFunc(`keyword.competition.level.${difficultyLevel}.disclaimer`, {
                        country,
                    }),
                };
            },
        };
    });
    return {
        ...data,
        Data: transformedData,
    };
};

export const getSearchEnginesList = (
    family: string,
    searchEngineList: IWebsiteKeywordsPageTableTopContext["searchEngines"],
    webSource: string,
): string[] => {
    if (!family?.length) {
        return;
    }
    const familyList = family.split(FILTER_DEFAULT_SEPARATOR);

    const searchEngines = familyList.reduce(
        (acc, item) => {
            const searchEngine = searchEngineList.find((st) => st.id === item)?.text;
            if (searchEngine !== undefined) {
                acc.result.push(searchEngine);
            }
            return acc;
        },
        { result: [] },
    );
    return webSource === devicesTypes.DESKTOP && searchEngines.result.length > 0 ? searchEngines.result : undefined;
};
